import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import NaturalNextSteps from "@/components/NaturalNextSteps";
import TopImage from "@/components/TopImage";
import AsideImage from "@/components/AsideImage";
import BodyText from "@/components/BodyText";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

function WaitlistForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/waitlist", { email, name: name || null, source: "pattern-reveal" });
      setDone(true);
      toast.success("You're on the list. Thank you.");
    } catch (err) {
      toast.error("We couldn't reach the server just now — please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-lg border border-brand-turquoise/30 bg-brand-turquoise/5 p-6 max-w-xl"
        data-testid="waitlist-success"
      >
        <p className="font-serif text-[20px] text-brand-ink">Thank you.</p>
        <p className="mt-2 text-[15px] text-brand-ink/70">
          When Pattern Reveal reaches meaningful milestones, you'll be among
          the first to hear.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-xl" data-testid="waitlist-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          data-testid="waitlist-name-input"
          className="w-full rounded-md border border-brand-rule bg-white/70 px-4 py-3 text-[15px] text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-blue"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          data-testid="waitlist-email-input"
          className="w-full rounded-md border border-brand-rule bg-white/70 px-4 py-3 text-[15px] text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-blue"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="waitlist-submit"
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-brand-cream text-[14px] tracking-wide hover:bg-brand-blue transition-colors disabled:opacity-60"
      >
        {loading ? "Sending…" : "Keep Me Informed"} <span aria-hidden>→</span>
      </button>
      <p className="mt-3 text-[12px] text-brand-ink/50">
        No marketing. Only occasional, thoughtful updates.
      </p>
    </form>
  );
}

export default function PatternReveal() {
  const t = useContent("pattern_reveal");
  useSeo("pattern_reveal", {
    title: "Pattern Reveal — Quantum Results",
    description:
      "Discover recurring patterns that may be quietly shaping your results.",
  });
  return (
    <div data-testid="page-pattern-reveal">
      <TopImage src={IMG.patternReveal.hero.src} alt={IMG.patternReveal.hero.alt} credit={IMG.patternReveal.hero.credit} testId="pattern-reveal-top-image" />
      <PageHero
        eyebrow={t("hero_eyebrow", "Pattern Reveal")}
        title={t("hero_title", <>Some ideas are worth taking the time to get <span className="text-brand-blue">right</span>.</>)}
        lede={t("hero_lede", "Pattern Reveal is my effort to create a thoughtful experience that helps people recognize the recurring patterns quietly shaping the way we think, decide, and live.")}
        testId="pr-hero"
      />

      <Section testId="pr-taking-shape">
        <BodyText
          className=""
          value={t.body(
            "taking_shape_body",
            `For many years, I've been fascinated by the recurring patterns that quietly shape the way we think, make decisions, and experience our lives.

Some patterns help us move forward with greater clarity and confidence. Others seem to quietly repeat themselves, influencing our choices and results without us fully recognizing they're there.

Pattern Reveal is my effort to create a thoughtful experience that helps people recognize those patterns for themselves.

It's an ambitious project, and I'm intentionally allowing it to evolve through research, observation, real-world experience, and continuous refinement.

**Because if Pattern Reveal is going to become what I believe it can become, I want to get it right.**`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("section2_eyebrow", "What if there are patterns you haven't yet seen?")}
          title={t("section2_title", "What if your life has been showing you something all along?")}
          testId="pr-patterns"
        >
          <BodyText
            className=""
            value={t.body(
              "section2_body",
              `Most of us can recognize recurring patterns once someone points them out.

Sometimes those patterns quietly support the life we want to create.

Sometimes they quietly limit what's possible.

The challenge isn't that the patterns are hidden. It's that we're often too close to our own lives to recognize them.

**What if learning to recognize those patterns could change the way you understand your past… and influence the choices you make moving forward?**`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("section3_eyebrow", "Recognition creates the possibility for insight")}
        title={t("section3_title", "Recognition creates the possibility for insight.")}
        testId="pr-recognition"
      >
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-start">
          <BodyText
            value={t.body(
              "section3_body",
              `It's difficult to change something we don't recognize.

We often spend years trying to improve our results without fully understanding the recurring patterns influencing the choices we make every day. Recognition changes that.

The moment we begin seeing a pattern clearly, we create the possibility for new insight. That insight doesn't tell us what to do. It simply allows us to understand ourselves more clearly.

And from greater understanding often comes different choices. Different choices create different experiences. Over time, those experiences begin shaping different results.`
            )}
          />
          <AsideImage
            src={IMG.patternReveal.recognition.src}
            alt={IMG.patternReveal.recognition.alt}
            aspect="aspect-[4/5]"
            testId="pattern-reveal-recognition-image"
          />
        </div>
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("section4_eyebrow", "An experience of discovery")}
          title={t("section4_title", "A new way of seeing yourself.")}
          testId="pr-discovery"
        >
          <BodyText
            className=""
            value={t.body(
              "section4_body",
              `Pattern Reveal is being designed as a guided experience of observation, reflection, and personal discovery.

Rather than telling you who you are, it will invite you to notice recurring patterns that may have been quietly shaping your experiences for years.

There won't be a score to achieve. No label to accept. No category you'll be asked to fit into.

Instead, you'll be invited to explore your own life with curiosity, recognizing patterns that are uniquely yours and allowing your own insights to emerge naturally.

**Because the most meaningful discoveries are rarely the ones someone tells us. They're the ones we recognize for ourselves.**`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("section5_eyebrow", "Why I'm building Pattern Reveal")}
        title={t("section5_title", "Some journeys deserve to be explored more deeply.")}
        testId="pr-why"
      >
        <BodyText
          className=""
          value={t.body(
            "section5_body",
            `Throughout my career, I've had the privilege of working with people from many different backgrounds, professions, and stages of life.

Again and again, I've noticed something remarkable. When people begin recognizing the recurring patterns shaping their experiences, they often see themselves with greater compassion, greater clarity, and greater possibility.

That moment of recognition has fascinated me for years.

Pattern Reveal is my effort to create an experience that makes those moments more accessible — not by providing answers, but by helping people ask new questions and discover new ways of seeing themselves.

I don't know exactly what Pattern Reveal will become over the coming months and years. But I do know why I'm building it.

**Because I believe understanding ourselves more deeply changes the way we experience our lives.**`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("join_eyebrow", "Join the journey")}
          title={t("join_title", "This is only the beginning.")}
          testId="pr-join"
        >
          <BodyText
            className=""
            value={t.body(
              "join_body",
              `Pattern Reveal is still evolving, and I believe that's one of its greatest strengths.

Every conversation, every observation, and every refinement helps shape what it will become.

If the ideas you've encountered here resonate with you, I'd love to keep you informed as Pattern Reveal continues to develop. When meaningful milestones are reached, new insights emerge, or the experience becomes available, you'll be among the first to know.

**Some journeys are worth following from the very beginning. Perhaps this is one of them.**`
            )}
          />
          <div className="mt-10">
            <WaitlistForm />
          </div>
        </Section>
      </section>

      <NaturalNextSteps
        items={[
          { to: "/four-levels", label: "The Four Levels Experience" },
          { to: "/weekly-quantum-edge", label: "Weekly Quantum Edge" },
          { to: "/contact", label: "Start a conversation" },
        ]}
      />
    </div>
  );
}
